/**
 * Low-level upsert primitives for managed <head> tags. Every element these
 * functions touch is tagged data-seo-managed so useHeadTags can clear exactly
 * its own tags on the next route change without touching the hand-authored
 * tags already in index.html (charset, viewport, favicon, manifest, ...).
 */

type MetaSelector = { attr: "name" | "property"; value: string };

function getOrCreateMeta({ attr, value }: MetaSelector): HTMLMetaElement {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${value}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, value);
    el.setAttribute("data-seo-managed", "meta");
    document.head.appendChild(el);
  }
  return el;
}

export function upsertMeta(selector: MetaSelector, content: string): void {
  getOrCreateMeta(selector).setAttribute("content", content);
}

export function removeMeta(selector: MetaSelector): void {
  document.head.querySelector(`meta[${selector.attr}="${selector.value}"][data-seo-managed]`)?.remove();
}

/**
 * Upserts a single-instance link (e.g. canonical). Matches by rel alone —
 * not just previously-managed elements — so it takes over the static
 * canonical tag already present in index.html instead of leaving it in the
 * DOM ahead of a newly-appended one (querySelector would keep returning the
 * stale original otherwise). Not for rel="alternate" (hreflang), which has
 * many coexisting instances — see appendManagedLink/removeManagedLinks.
 */
export function upsertLink(rel: string, attrs: Record<string, string>): HTMLLinkElement {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("data-seo-managed", "link");
  for (const [key, val] of Object.entries(attrs)) {
    el.setAttribute(key, val);
  }
  return el;
}

/**
 * Removes every link with this rel (used for the hreflang block, which has one element per locale).
 *
 * Matches by rel alone rather than [data-seo-managed], for the same reason upsertLink does: the
 * prerendered HTML carries its own hreflang cluster, written by generate-static-meta.ts and
 * unmarked. Filtering on the marker left that cluster in place and appended a second one, so every
 * hydrated page shipped each alternate twice. index.html hand-authors no rel="alternate", so
 * there is nothing else here to preserve.
 */
export function removeManagedLinks(rel: string): void {
  document.head.querySelectorAll(`link[rel="${rel}"]`).forEach((el) => el.remove());
}

/**
 * Appends a new managed link unconditionally — used for hreflang, where
 * several elements share rel="alternate" and must coexist. Callers are
 * expected to call removeManagedLinks(rel) first to clear the previous set.
 */
export function appendManagedLink(rel: string, attrs: Record<string, string>): void {
  const el = document.createElement("link");
  el.setAttribute("rel", rel);
  el.setAttribute("data-seo-managed", "link");
  for (const [key, val] of Object.entries(attrs)) {
    el.setAttribute(key, val);
  }
  document.head.appendChild(el);
}

export function upsertJsonLd(data: object | object[] | undefined): void {
  const existing = document.head.querySelector('script[data-seo-managed="jsonld"]');
  if (!data) {
    existing?.remove();
    return;
  }
  const script = existing ?? document.createElement("script");
  script.setAttribute("type", "application/ld+json");
  script.setAttribute("data-seo-managed", "jsonld");
  script.textContent = JSON.stringify(data);
  if (!existing) document.head.appendChild(script);
}
